import React, { useState, useRef, useEffect } from "react";
import { Box, Chip, Button, Stack, Loading } from "@curves/ui-base";
import { Html5QrcodeSupportedFormats, Html5Qrcode } from "html5-qrcode";
import { v4 as uuid } from "uuid";
import { isEmpty, getIsMobile } from "@util/utils";

/**
 * 手機載具條碼
 * @param {{
 *   onScanSuccess?: (decodedText: string) => void,
 *   onScanError?: (errorMessage: string) => void,
 *   hasButton: boolean
 *  }} props - 掃描成功與錯誤的回呼函式
 */
const MobileCarrierBarCode = ({ onScanSuccess = () => {}, onScanError = () => {}, hasButton = false }) => {
	const [orientationKey, setOrientationKey] = useState(0);
	const [targetDom] = useState(`qr-reader-${uuid()}`);
	const [barCodeValue, setBarCodeValue] = useState(null);
	const [isScanning, setIsScanning] = React.useState(false);
	const [cameras, setCameras] = useState([]);
	const [currentCameraId, setCurrentCameraId] = useState(null);
	const [isRequestingPermission, setIsRequestingPermission] = useState(false);
	const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
	const html5QrCode = useRef(null);
	const lastScannedValue = useRef(null);
	const orientationTimeoutRef = useRef(null);

	/**
	 * 掃描成功的回調函式
	 * @param {string} decodedText 掃描到的文字
	 * @param {object} _decodedResult 掃描結果的詳細資訊
	 */
	const onScanResult = (decodedText, _decodedResult) => {
		// 避免重複掃描相同的值
		if (decodedText !== lastScannedValue.current) {
			lastScannedValue.current = decodedText;
			setBarCodeValue(decodedText);
			onScanSuccess(decodedText);
		}
	};

	/**
	 * 掃描錯誤的回調函式
	 * @param {string} errorMessage 錯誤訊息
	 */
	const qrCodeErrorCallback = errorMessage => {
		// 忽略常見的找不到條碼錯誤
		if (!errorMessage.includes("NotFoundException") && !errorMessage.includes("No MultiFormat Readers") && !errorMessage.includes("IndexSizeError")) {
			onScanError(errorMessage);
			console.log("⚠️ 掃描錯誤:", errorMessage);
		}
	};

	/**
	 * 切換鏡頭
	 * @returns {Promise<void>}
	 */
	const switchCamera = async () => {
		if (cameras.length < 2 || isSwitchingCamera) return;

		setIsSwitchingCamera(true);

		try {
			const currentIndex = cameras.findIndex(cam => cam.id === currentCameraId);
			const nextIndex = (currentIndex + 1) % cameras.length;
			const nextCameraId = cameras[nextIndex].id;

			// 先停止當前掃描
			if (html5QrCode.current && html5QrCode.current.isScanning) {
				await html5QrCode.current.stop();
			}

			// 清理舊的實例
			html5QrCode.current.clear();

			// 短暫延遲確保資源釋放
			await new Promise(resolve => setTimeout(resolve, 300));

			// 建立新實例並開始掃描
			await initializeScanner(nextCameraId);
		} catch (error) {
			console.error("切換鏡頭失敗:", error);
			// 如果切換失敗，嘗試重新啟動原本的鏡頭
			await initializeScanner(currentCameraId);
		} finally {
			setIsSwitchingCamera(false);
		}
	};

	/**
	 * 檢查相機權限狀態
	 */
	const checkCameraPermission = async () => {
		try {
			const result = await navigator.permissions.query({ name: "camera" });
			// console.log("相機權限狀態:", result.state);
			return result.state;
		} catch (error) {
			console.log("無法檢查權限狀態");
			return "unknown";
		}
	};

	/**
	 * 取得最適合使用的攝影機裝置
	 * @param {Array<{ id: string, label: string }>} cameras - 攝影機裝置清單
	 * @returns {{ id: string, label: string } | undefined} - 最佳選擇的攝影機裝置
	 */
	const getPreferredCamera = cameras => {
		if (cameras.length === 1) {
			return cameras[0];
		}
		// 優先找後鏡頭
		const backCamera = cameras.find(cam => /back|rear|environment|wide|後置|後鏡/i.test(cam.label) && !/ultrawide|超廣角/i.test(cam.label));
		return backCamera || cameras.at(-1);
	};

	/**
	 * 取得相機配置
	 * @param {string} cameraId - 相機ID
	 * @returns {object} - 配置物件
	 */
	const getCameraConfig = cameraId => {
		const isMobile = getIsMobile();

		// 基本配置
		const baseConfig = {
			fps: 10,
			qrbox: isMobile ? { width: 320, height: 140 } : { width: 400, height: 180 },
			formatsToSupport: [Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.CODE_39],
			// 關鍵設定：避免鏡像翻轉問題
			disableFlip: false,
			// 自動旋轉修正
			experimentalFeatures: {
				useBarCodeDetectorIfSupported: true,
			},
		};

		// 針對不同相機調整設定
		const camera = cameras.find(cam => cam.id === cameraId);
		if (camera) {
			// 前鏡頭通常需要鏡像
			if (/front|user|前置|前鏡/i.test(camera.label)) {
				baseConfig.disableFlip = false;
			} else {
				// 後鏡頭不需要鏡像
				baseConfig.disableFlip = true;
			}
		}

		return baseConfig;
	};

	/**
	 * 重新初始化掃描器
	 * @param {string} cameraId - 相機ID
	 * @returns {Promise<void>}
	 */
	const initializeScanner = async cameraId => {
		try {
			// 建立新實例
			html5QrCode.current = new Html5Qrcode(targetDom);
			// 取得針對該相機的配置
			const config = getCameraConfig(cameraId);
			// 開始掃描
			await html5QrCode.current.start(cameraId, config, onScanResult, qrCodeErrorCallback);
			setCurrentCameraId(cameraId);
			setIsScanning(true);
			// console.log(`已切換至相機: ${cameras.find(c => c.id === cameraId)?.label || cameraId}`);
		} catch (error) {
			console.error("啟動相機失敗:", error);
			throw error;
		}
	};

	/**
	 * 啟動掃描功能
	 * @param {string|null} cameraId - 指定的相機ID
	 * @returns {Promise<void>}
	 */
	const startScanning = async (cameraId = null) => {
		setIsRequestingPermission(true);

		const permission = await checkCameraPermission();
		if (permission === "denied") {
			console.error("相機權限被拒絕，請到設定中開啟");
			setIsRequestingPermission(false);
			return;
		}

		try {
			const availableCameras = await Html5Qrcode.getCameras();

			if (availableCameras && availableCameras.length) {
				// console.log("可用相機列表:");
				// availableCameras.forEach((cam, idx) => {
				// 	console.log(`[${idx}] ${cam.label || cam.id}`);
				// });

				// 過濾超廣角鏡頭
				const filteredCameras = availableCameras.filter(cam => !/ultrawide|超廣角/i.test(cam.label));
				setCameras(filteredCameras);

				// 選擇相機
				const preferredCamera = getPreferredCamera(filteredCameras);
				const selectedId = cameraId || preferredCamera?.id;

				// 開始掃描
				await initializeScanner(selectedId);
			} else {
				console.error("沒有找到可用的相機");
			}
		} catch (error) {
			console.error("無法啟動相機:", error);
		} finally {
			setIsRequestingPermission(false);
		}
	};

	/**
	 * 停止掃描功能
	 * @returns {Promise<void>}
	 */
	const stopScanning = async () => {
		try {
			if (html5QrCode.current) {
				if (html5QrCode.current.isScanning) {
					await html5QrCode.current.stop();
				}
				html5QrCode.current.clear();
			}
			setBarCodeValue(null);
			lastScannedValue.current = null;
			setIsScanning(false);
		} catch (error) {
			console.error("停止掃描時發生錯誤:", error);
		}
	};

	/**
	 * 重置掃描結果
	 */
	const resetScanResult = () => {
		setBarCodeValue(null);
		lastScannedValue.current = null;
		onScanSuccess(null);
	};

	/**
	 * 處理螢幕方向變化
	 */
	const handleOrientationChange = async () => {
		// 清除之前的 timeout
		if (orientationTimeoutRef.current) {
			clearTimeout(orientationTimeoutRef.current);
		}

		orientationTimeoutRef.current = setTimeout(async () => {
			try {
				// 停止當前掃描
				if (html5QrCode.current && html5QrCode.current.isScanning) {
					await html5QrCode.current.stop();
				}
				html5QrCode.current.clear();
			} catch (error) {
				console.error("停止掃描錯誤:", error);
			}

			// 強制重新渲染整個組件
			setOrientationKey(prev => prev + 1);

			// 重新渲染後自動開始掃描
			if (!hasButton) {
				setTimeout(() => {
					startScanning(currentCameraId);
				}, 100);
			}
		}, 500); // 等待旋轉動畫完成
	};

	/**
	 * 防抖處理函式
	 */
	const debounce = (func, wait) => {
		let timeout;
		return function executedFunction(...args) {
			const later = () => {
				clearTimeout(timeout);
				func(...args);
			};
			clearTimeout(timeout);
			timeout = setTimeout(later, wait);
		};
	};

	useEffect(
		() => {
			// 如果沒有按鈕，則自動開始掃描
			if (!hasButton) {
				startScanning();
			}

			// 建立防抖的方向變化處理函式
			const debouncedOrientationHandler = debounce(handleOrientationChange, 300);

			// 監聽螢幕方向變化
			window.addEventListener("orientationchange", debouncedOrientationHandler);

			// 監聽視窗大小變化（某些裝置不支援 orientationchange）
			window.addEventListener("resize", debouncedOrientationHandler);

			// 組件卸載時清理
			return () => {
				// 移除事件監聽
				window.removeEventListener("orientationchange", debouncedOrientationHandler);
				window.removeEventListener("resize", debouncedOrientationHandler);

				// 清理相機資源
				if (html5QrCode.current) {
					html5QrCode.current
						.stop()
						.catch(err => console.log("掃描停止錯誤:", err))
						.finally(() => {
							if (html5QrCode.current) {
								html5QrCode.current.clear();
							}
						});
				}
			};
		},
		// eslint-disable-next-line
		[]
	);

	return (
		<React.Fragment key={orientationKey}>
			{/* 自訂的控制按鈕 */}
			{hasButton && (
				<Box style={{ marginBottom: 16 }}>
					{!isScanning ? (
						<Button variant="outlined" onClick={() => startScanning()} disabled={isRequestingPermission}>
							開始掃描
						</Button>
					) : (
						<Button variant="outlined" color="error" onClick={stopScanning} disabled={isSwitchingCamera}>
							停止掃描
						</Button>
					)}
				</Box>
			)}

			<Box style={{ width: 500, maxWidth: "100%", margin: "auto", position: "relative", borderRadius: 20 }}>
				<Chip className="mb-2" size="large" label={`手機載具：${barCodeValue || "等待掃描..."}`} color={isEmpty(barCodeValue) ? "error" : "success"} />
				<Stack
					style={{
						position: "absolute",
						zIndex: 10,
						top: 0,
						right: 0,
					}}
				>
					{/* 掃描結果重置按鈕 */}
					{barCodeValue && (
						<Button onClick={resetScanResult} variant="text" size="small" style={{ marginLeft: 8 }}>
							清除
						</Button>
					)}

					{/* 鏡頭切換按鈕 */}
					{isScanning && cameras.length > 1 && (
						<Button onClick={switchCamera} variant="outlined" size="small" disabled={isSwitchingCamera}>
							{isSwitchingCamera ? "切換中..." : "切換鏡頭"}
						</Button>
					)}
				</Stack>

				<Box
					style={{
						borderRadius: 10,
						overflow: "hidden",
						boxShadow: "2px 4px 16px rgba(0, 0, 0, 0.3)",
						position: "relative",
					}}
				>
					{/* 載入中提示 */}
					{(isRequestingPermission || isSwitchingCamera) && (
						<Box
							style={{
								backgroundColor: "rgba(0, 0, 0, 0.48)",
								height: 300,
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								zIndex: 5,
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Loading style={{ width: 50 }} size={50} duration={500} />
							<Box style={{ color: "#fff", marginTop: 16 }}>
								{isRequestingPermission && "請求相機權限..."}
								{isSwitchingCamera && "切換鏡頭中..."}
							</Box>
						</Box>
					)}

					{/* 掃描區域 */}
					<Box id={targetDom} style={{ position: "relative", overflow: "hidden" }}></Box>
				</Box>
			</Box>
		</React.Fragment>
	);
};

export default MobileCarrierBarCode;
